'use client';

import { ActionBarPrimitive, MessagePrimitive } from '@assistant-ui/react';
import { type JSX } from 'react';

import { KeywordSearchLink } from '../KeywordSearchLink';

/**
 * Inline SVG icons. The design system's <Icon> component dynamically imports each
 * SVG and shows a <Skeleton> placeholder while loading — that causes a visible
 * layout shift when this card appears. Rendering the SVGs inline keeps the error
 * card paint-stable on first render.
 */

function ErrorCircleIcon(): JSX.Element {
    return (
        <svg
            width={20}
            height={20}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="shrink-0 text-red-600"
            data-testid="icon-error"
            aria-hidden="true"
        >
            <path
                fill="currentColor"
                d="M12 0a12 12 0 1 0 12 12A12.014 12.014 0 0 0 12 0m-1.286 6a1.286 1.286 0 0 1 2.572 0v5.143a1.285 1.285 0 1 1-2.572 0zM12 18.857a1.714 1.714 0 1 1 0-3.428 1.714 1.714 0 0 1 0 3.428"
            />
        </svg>
    );
}

function RefreshIcon({ className }: { className?: string }): JSX.Element {
    return (
        <svg
            width={16}
            height={16}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={className}
            aria-hidden="true"
        >
            <path
                fill="currentColor"
                d="M6.18 6.17a8.22 8.22 0 0 1 8.35-2 1.25 1.25 0 1 0 .76-2.38A10.75 10.75 0 0 0 2.05 16a.25.25 0 0 1-.1.3l-1.4.93a1 1 0 0 0-.43 1 1 1 0 0 0 .78.79l4.41.98h.2a1 1 0 0 0 .55-.17 1 1 0 0 0 .43-.63l.91-4.41a1 1 0 0 0-.42-1 1 1 0 0 0-1.11 0l-1.34.88a.29.29 0 0 1-.317-.063.3.3 0 0 1-.063-.097 8.28 8.28 0 0 1 2.03-8.34"
            />
            <path
                fill="currentColor"
                d="M23.88 5.83a1 1 0 0 0-.76-.8L18.73 4a1 1 0 0 0-1.2.75l-1 4.38a1 1 0 0 0 .4 1 1 1 0 0 0 .58.19.94.94 0 0 0 .53-.16l1.44-.9a.29.29 0 0 1 .38.16A8.25 8.25 0 0 1 9.57 19.88a1.25 1.25 0 0 0-1.57.83 1.24 1.24 0 0 0 .82 1.56 10.6 10.6 0 0 0 3.19.48A10.75 10.75 0 0 0 22 8a.27.27 0 0 1 .1-.31l1.35-.84a1 1 0 0 0 .43-1.02"
            />
        </svg>
    );
}

function SearchIcon({ className }: { className?: string }): JSX.Element {
    return (
        <svg
            width={16}
            height={16}
            viewBox="0 0 25 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={className}
            aria-hidden="true"
        >
            <path
                fill="currentColor"
                fillRule="evenodd"
                clipRule="evenodd"
                d="m19.718 15.943 4.642 4.643c.78.78.78 2.045 0 2.826a2.024 2.024 0 0 1-2.826 0l-4.642-4.641a10.25 10.25 0 1 1 2.826-2.828m-1.268-5.687a7.246 7.246 0 0 0-7.246-7.246 7.254 7.254 0 0 0-7.245 7.246 7.246 7.246 0 1 0 14.49 0"
            />
        </svg>
    );
}

/**
 * Error card shown in the chat thread when the MCP/chatbot fails to return a response.
 * Offers retry (re-submits last user message via ActionBarPrimitive.Reload) and keyword search fallback.
 */
export function ChatErrorCard(): JSX.Element {
    return (
        <MessagePrimitive.Root className="py-3" data-role="assistant">
            <div className="mx-auto w-full max-w-[90%] rounded-lg bg-red-100 p-4">
                <div className="mb-4 flex flex-col items-center gap-1">
                    <div className="flex items-center gap-2">
                        <ErrorCircleIcon />
                        <p className="text-base font-semibold leading-snug text-red-950">Hmm, something went wrong</p>
                    </div>
                    <p className="text-sm leading-5 text-red-950">We weren&apos;t able to process your request</p>
                </div>
                <div className="flex flex-col gap-2">
                    <ActionBarPrimitive.Reload asChild>
                        <button
                            type="button"
                            className="flex w-full items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-2.5 text-sm font-semibold leading-5 text-white transition-colors hover:bg-red-700"
                        >
                            <RefreshIcon className="text-white" />
                            Try Again
                        </button>
                    </ActionBarPrimitive.Reload>
                    <KeywordSearchLink className="flex w-full items-center justify-center gap-2 rounded-lg border border-gray-800 bg-transparent px-4 py-2.5 text-sm font-semibold text-gray-800 transition-colors hover:bg-red-200">
                        <SearchIcon className="text-gray-800" />
                        Use Keyword Search
                    </KeywordSearchLink>
                </div>
            </div>
        </MessagePrimitive.Root>
    );
}
